// usePitchMode — demo-only toggle that hides platform-noise UI from the main
// TA dashboard (Bug Report button, "Issue Tracker" sidebar entry, StatusBanner
// system/perf rows, FiveDsDashboard "Site Performance Analysis" card) without
// removing any underlying functionality. Resolution order:
//   1. localStorage key `spartis.pitchMode` if set ('1'/'true' = on, '0'/'false' = off)
//   2. env default `REACT_APP_PITCH_MODE` ('1' or 'true', case-insensitive)
//   3. off
// Live updates flow via `setPitchMode()` (dispatches `spartis:pitchModeChanged`)
// and the cross-tab `storage` event, so toggling from anywhere flips all hooks
// immediately without a reload.
import { useEffect, useState } from 'react';

const STORAGE_KEY = 'spartis.pitchMode';
const EVENT_NAME = 'spartis:pitchModeChanged';

function readEnvDefault(): boolean {
  const v = process.env.REACT_APP_PITCH_MODE;
  if (!v) return false;
  return v === '1' || v.toLowerCase() === 'true';
}

function readCurrent(): boolean {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === '1' || stored === 'true') return true;
    if (stored === '0' || stored === 'false') return false;
  } catch {
  }
  return readEnvDefault();
}

// Non-hook, one-shot read of the current pitch-mode state, for code that
// runs outside a React render (e.g. auto-selection logic in data hooks).
export function isPitchModeActive(): boolean {
  return readCurrent();
}

export function setPitchMode(on: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
  } catch {
  }
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: on }));
  } catch {
  }
}

export function clearPitchModeOverride(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
  }
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: readEnvDefault() }));
  } catch {
  }
}

export function usePitchMode(): boolean {
  const [on, setOn] = useState<boolean>(() => {
    if (typeof window === 'undefined') return readEnvDefault();
    return readCurrent();
  });

  useEffect(() => {
    const handler = () => setOn(readCurrent());
    window.addEventListener(EVENT_NAME, handler as EventListener);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener(EVENT_NAME, handler as EventListener);
      window.removeEventListener('storage', handler);
    };
  }, []);

  return on;
}
